/* script */
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

// Components
import LoaderButton from 'shared/ui/components/form-components/LoaderButton.vue';

/**
 * This component implements a wizard stepper.
 *
 * Contract:
 * - wizard steps must implement a `validate` method
 *
 * Emits:
 * - `wizard:complete` with the collected data as payload when all the steps components are validated truthfully.
 *
 * @vue-prop {Object} initialData the initial form to start the wizard with.
 * @vue-prop {Object[]} steps wizard steps objects array defined as follow:
 *
 * @vue-prop {String} step.title the title of the step.
 * @vue-prop {Object} step.component the vue component to use for the step.
 * @vue-prop {String} [step.nextLabel='wizards.next'] an i18n key to use for next step button
 * @vue-prop {String} [step.prevLabel='wizards.prev'] an i18n key to use for previous step button
 * @vue-prop {Boolean} [step.active=1] whether the step should be the active one
 *
 * @example
 * Wizard(:steps="steps" @wizard:completed="submitForm")
 *
 * @category ui.components
 * @exports Wizard
 * @component
 */
const __vue_script__ = {
    name: 'Wizard',

    components: { LoaderButton },

    props: {
        initialData: {
            type: Object,
            default: () => {}
        },

        steps: {
            type: Array,
            required: true
        },

        keepAliveExclude: {
            type: [String, RegExp, Array],
            default: null
        }
    },

    data() {
        return {
            currentStep: 1,
            enableNextStep: false,
            form: this.initialData || {},
            loading: false
        };
    },

    computed: {
        /**
         * Determines if the previous page button should be visble or not.
         *
         * @returns {Boolean} whether to show the previous step or not.
         */
        showPrev() {
            return this.currentStep !== 1;
        },

        /**
         * Calculates the current wizard page starting from the current step.
         *
         * @returns {Object} the current wizard step page object.
         */
        currentPage() {
            return this.steps[this.currentStep - 1] || {};
        },

        /**
         * Calculates the current wizard component parameters.
         *
         * @returns {Object} the component extra parameters object.
         */
        currentParams() {
            return this.currentPage.params || { form: this.form };
        },

        /**
         * Determines if the wizard has more steps.
         *
         * @returns {Boolean} whether the wizard has more steps or not.
         */
        hasMoreSteps() {
            return !!this.steps[this.currentStep];
        }
    },

    watch: {
        currentPage() {
            this.setModalContentStyle();
        }
    },

    created() {
        for (const [i, step] of this.steps.entries()) {
            if (step.active) {
                this.currentStep = i + 1;
            }
        }
    },

    mounted() {
        this.setModalContentStyle();
    },

    methods: {
        /**
         * Sets the modal content style if defined
         */
        setModalContentStyle() {
            this.$utils.modals.setModalContentStyle(this.$el, this.currentPage.modalContentStyle);
        },

        /**
         * Moves the scrollable part of the wizard viewport to the top.
         */
        scrollTop() {
            this.$refs.scrollable.scroll(0, 0);
        },

        /**
         * Moves the wizard to previous step
         */
        prev() {
            this.changePage(-1);
            this.enableNextStep = true;
            this.scrollTop();
        },

        /**
         * Moves the wizard to next step or emits the wizard completion.
         */
        async next() {
            this.loading = true;

            if (await this.$refs.step.submit()) {

                if (!this.hasMoreSteps) {
                    // call change page to save the data, but do not increment the counter
                    this.changePage(0);
                    this.$emit('wizard:completed', this.form);
                    return;
                }

                this.changePage(1);
                this.scrollTop();

            } else {
                console.warn('[Wizard] Current step not valid, can\'t proceed', this.$refs.step.$errors);
            }

            this.loading = false;
        },

        changePage(increment) {
            this.currentPage.data = this.$refs.step.form;
            console.log('[Wizard] Collected data on step #%d:', this.currentStep, this.currentPage.data);

            // Increment wizard form with new collected data;
            this.form = Object.assign(this.form, this.currentPage.data);

            // Move step forward...
            this.currentStep += increment;

            // ...and deliver the whole form.
            this.currentParams.form = this.form;
        },

        /**
         * Moves the wizard to the specified step.
         *
         * @param {Number} the step number to go to
         * @param {String} [field] an optional field to focus
         */
        goTo(stepNumber, field) {
            this.currentStep = stepNumber;
            this.currentParams.focus = field;
        },

        /**
         * This handler is called when the form validity changes.
         *
         * @param {Boolean} valid whether the inner form is valid or not
         */
        toggleNextButton(valid) {
            this.enableNextStep = valid;
        }
    }
};

/* template */
var __vue_render__ = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{staticClass:"wizard modal-wrapper"},[_c('div',{staticClass:"modal-header"},[_vm._t("default"),(_vm.steps.length > 1)?_c('ul',{staticClass:"wizard-steps"},_vm._l((_vm.steps),function(step,index){return _c('li',{key:step.id,class:{active: (index + 1) === _vm.currentStep}},[_c('span',{staticClass:"step"},[_vm._v(_vm._s(index + 1))]),_c('span',{staticClass:"title"},[_vm._v(_vm._s(_vm.$t(step.title)))])])}),0):_vm._e()],2),_c('div',{ref:"scrollable",staticClass:"modal-body"},[_c('div',{staticClass:"steps"},[_c('keep-alive',{attrs:{"exclude":_vm.keepAliveExclude}},[_c(_vm.currentPage.component,_vm._b({ref:"step",tag:"component",attrs:{"params":_vm.currentParams},on:{"step:go":_vm.goTo,"validity":_vm.toggleNextButton}},'component',_vm.currentPage.props,false))],1)],1)]),_c('div',{staticClass:"modal-footer"},[_vm._t("footer"),_c('div',{staticClass:"back-forth-buttons"},[(_vm.showPrev)?_c('button',{staticClass:"btn default",on:{"click":function($event){return _vm.prev()}}},[_vm._v(_vm._s(_vm.$t( this.currentPage.prevLabel || 'wizards.prev' )))]):_vm._e(),_c('loader-button',{staticClass:"primary",attrs:{"disabled":!_vm.enableNextStep,"status":_vm.loading},on:{"click":function($event){return _vm.next()}}},[_vm._v(_vm._s(_vm.$t( this.currentPage.nextLabel || 'wizards.next' )))])],1)],2)])}
var __vue_staticRenderFns__ = []

  /* style */
  const __vue_inject_styles__ = undefined
  /* scoped */
  const __vue_scope_id__ = undefined
  /* module identifier */
  const __vue_module_identifier__ = undefined
  /* functional template */
  const __vue_is_functional_template__ = false
  /* component normalizer */
  function __vue_normalize__(
    template, style, script,
    scope, functional, moduleIdentifier, shadowMode,
    createInjector, createInjectorSSR, createInjectorShadow
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {}

    // For security concerns, we use only base name in production mode.
    component.__file = "Wizard.vue"

    if (!component.render) {
      component.render = template.render
      component.staticRenderFns = template.staticRenderFns
      component._compiled = true

      if (functional) component.functional = true
    }

    component._scopeId = scope

    if (false) {
      let hook
      if (false) {
        // In SSR.
        hook = function(context) {
          // 2.3 injection
          context =
            context || // cached call
            (this.$vnode && this.$vnode.ssrContext) || // stateful
            (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext) // functional
          // 2.2 with runInNewContext: true
          if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
            context = __VUE_SSR_CONTEXT__
          }
          // inject component styles
          if (style) {
            style.call(this, createInjectorSSR(context))
          }
          // register component module identifier for async chunk inference
          if (context && context._registeredComponents) {
            context._registeredComponents.add(moduleIdentifier)
          }
        }
        // used by ssr in case component is cached and beforeCreate
        // never gets called
        component._ssrRegister = hook
      }
      else if (style) {
        hook = shadowMode 
          ? function(context) {
              style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot))
            }
          : function(context) {
              style.call(this, createInjector(context))
            }
      }

      if (hook !== undefined) {
        if (component.functional) {
          // register for functional component in vue file
          const originalRender = component.render
          component.render = function renderWithStyleInjection(h, context) {
            hook.call(context)
            return originalRender(h, context)
          }
        } else {
          // inject component registration as beforeCreate hook
          const existing = component.beforeCreate
          component.beforeCreate = existing ? [].concat(existing, hook) : [hook]
        }
      }
    }

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__ = /*#__PURE__*/__vue_normalize__(
    { render: __vue_render__, staticRenderFns: __vue_staticRenderFns__ },
    __vue_inject_styles__,
    __vue_script__,
    __vue_scope_id__,
    __vue_is_functional_template__,
    __vue_module_identifier__,
    false,
    undefined,
    undefined,
    undefined
  )

  export default __vue_component__